/**
 * The profile page on screen (src/profile): the picture is the whole screen — you and your motor in the
 * lock-up yard (profileScene.ts) — and the words sit round its edges on dark glass:
 *
 *  - top left: the kicker (where and when), your name in the headline face, the title the borough gives you;
 *  - right: the record card — notoriety stars, a handful of lines from the record, your motor and Pike's work;
 *  - top right: the ✕ (one close ring for every sheet); bottom left: Snapshot and the hint.
 *
 * Drag the picture to look round, pinch or the wheel to come closer; it drifts back on its own. Snapshot hides
 * every panel for a clean picture (tap anywhere to bring them back) and hands the frame to the share sheet or a
 * download where the browser allows. Keys / pad: ← → turn, ↑ ↓ tilt, Esc leaves. Touch-first: every target is
 * at least 44 px, the card scrolls when a short phone can't hold it. It only draws; what the data means is
 * profileStats.ts, what the scene does is profileSystem.ts.
 */
import { el, esc, html, setText, toggle } from '../ui/dom';
import type { NavAction } from '../ui/screens';
import type { ModuleOverlay } from '../ui/uiTypes';
import { starsText, type CarCaption, type Notoriety, type ProfileRow } from './profileStats';
import './profile.css';

export interface ProfileData {
  /** "LOW BOROUGH · OCTOBER 1970" / the city online. */
  kicker: string;
  name: string;
  /** The line under the name (a title the borough gave you), or null. */
  title: string | null;
  notoriety: Notoriety;
  rows: ProfileRow[];
  car: CarCaption | null;
  /** When there is no motor: where to get one. */
  carNote: string | null;
}

export interface ProfileController {
  touch(): boolean;
  close(): void;
  orbit(dYaw: number, dPitch: number): void;
  zoom(factor: number): void;
  /** Clean picture on / off (the system also captures the frame on the way in). */
  snapshot(on: boolean): void;
  sound(s: 'tick' | 'click' | 'back' | 'whoosh' | 'denied'): void;
}

const css = (hex: number): string => `#${hex.toString(16).padStart(6, '0')}`;
const icon = (inner: string, size = 18): string =>
  `<svg viewBox="0 0 24 24" width="${size}" height="${size}" fill="none" stroke="currentColor" stroke-width="2.1" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">${inner}</svg>`;
/** A box camera (original line drawing). */
const CAMERA = icon('<rect x="3" y="7" width="18" height="12" rx="2"/><circle cx="12" cy="13" r="3.2"/><path d="M8 7l1.5-2.5h5L16 7"/>');

export class ProfileScreen implements ModuleOverlay {
  readonly root: HTMLElement;
  private readonly head: HTMLElement;
  private readonly card: HTMLElement;
  private readonly foot: HTMLElement;
  private readonly closeBtn: HTMLButtonElement;
  private readonly back: HTMLButtonElement;
  private clean = false;
  private readonly pointers = new Map<number, { x: number; y: number }>();
  private pinch = 0;
  private moved = 0;
  private readonly onKey: (e: KeyboardEvent) => void;

  constructor(
    parent: HTMLElement,
    private readonly c: ProfileController,
  ) {
    this.root = el('div', 'pf-screen', undefined, parent);
    this.root.setAttribute('role', 'dialog');
    this.root.setAttribute('aria-label', 'Your profile');
    toggle(this.root, 'touch', c.touch());
    // ---- the picture: orbit by drag, pinch / wheel to come closer
    const stage = el('div', 'pf-stage', undefined, this.root);
    stage.addEventListener('pointerdown', (e) => {
      stage.setPointerCapture?.(e.pointerId);
      this.pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      this.pinch = this.spread();
      this.moved = 0;
    });
    stage.addEventListener('pointermove', (e) => {
      const p = this.pointers.get(e.pointerId);
      if (!p) return;
      if (this.pointers.size >= 2) {
        p.x = e.clientX;
        p.y = e.clientY;
        const d = this.spread();
        if (this.pinch > 0 && d > 0) c.zoom(this.pinch / d);
        this.pinch = d;
        this.moved += 10;
        return;
      }
      this.moved += Math.abs(e.clientX - p.x) + Math.abs(e.clientY - p.y);
      c.orbit(-(e.clientX - p.x) * 0.35, (e.clientY - p.y) * 0.2);
      p.x = e.clientX;
      p.y = e.clientY;
    });
    const up = (e: PointerEvent): void => {
      this.pointers.delete(e.pointerId);
      this.pinch = this.spread();
      // A tap (no drag) on a clean picture brings the panels back.
      if (this.clean && this.moved < 6 && this.pointers.size === 0) this.setClean(false);
    };
    stage.addEventListener('pointerup', up);
    stage.addEventListener('pointercancel', up);
    stage.addEventListener(
      'wheel',
      (e) => {
        e.preventDefault();
        c.zoom(Math.exp(Math.max(-1, Math.min(1, e.deltaY * 0.0015))));
      },
      { passive: false },
    );
    el('div', 'pf-vignette', undefined, this.root);

    // ---- the words
    this.head = el('div', 'pf-head', undefined, this.root);
    this.card = el('aside', 'pf-card bh-glass', undefined, this.root);
    this.card.dataset.touchScroll = '';
    this.card.setAttribute('aria-label', 'The record');
    this.closeBtn = el('button', 'pf-close bh-x', '✕', this.root);
    this.closeBtn.type = 'button';
    this.closeBtn.setAttribute('aria-label', 'Close');
    this.closeBtn.title = 'Close (Esc)';
    this.closeBtn.addEventListener('click', (e) => {
      e.stopPropagation();
      c.sound('back');
      c.close();
    });
    this.foot = el('div', 'pf-foot', undefined, this.root);
    const snap = html('button', 'pf-snap pf-glass', `${CAMERA}<span>Snapshot</span>`, this.foot);
    snap.type = 'button';
    snap.setAttribute('aria-label', 'Snapshot: a clean picture without the panels');
    snap.addEventListener('click', (e) => {
      e.stopPropagation();
      c.sound('click');
      this.setClean(true);
    });
    el('span', 'pf-hint', c.touch() ? 'Drag to look round · pinch to come closer' : 'Drag to look round · wheel to come closer · ← → ↑ ↓', this.foot);
    // Clean picture: one small pill stays on screen so there is always a way back.
    this.back = el('button', 'pf-back pf-glass', 'Show the panels', this.root);
    this.back.type = 'button';
    this.back.hidden = true;
    this.back.addEventListener('click', (e) => {
      e.stopPropagation();
      this.setClean(false);
    });

    this.onKey = (e: KeyboardEvent): void => {
      if (e.code === 'KeyQ' || e.code === 'BracketLeft') c.orbit(-12, 0);
      else if (e.code === 'KeyE' || e.code === 'BracketRight') c.orbit(12, 0);
      else if (e.code === 'Minus') c.zoom(1.15);
      else if (e.code === 'Equal') c.zoom(0.87);
      else return;
      e.preventDefault();
    };
    window.addEventListener('keydown', this.onKey);
  }

  private spread(): number {
    if (this.pointers.size < 2) return 0;
    const [a, b] = [...this.pointers.values()];
    return Math.hypot(a!.x - b!.x, a!.y - b!.y);
  }

  /** The panels off (a clean picture) or on. */
  setClean(on: boolean): void {
    if (this.clean === on) return;
    this.clean = on;
    toggle(this.root, 'clean', on);
    this.back.hidden = !on;
    this.c.snapshot(on);
  }

  get isClean(): boolean {
    return this.clean;
  }

  render(d: ProfileData): void {
    this.head.innerHTML = `<span class="pf-kicker">${esc(d.kicker)}</span><h1>${esc(d.name)}</h1>${d.title ? `<span class="pf-title">${esc(d.title)}</span>` : ''}`;
    const n = d.notoriety;
    const parts: string[] = [];
    parts.push(
      `<div class="pf-noto"><span class="pf-cap">Notoriety</span><span class="pf-stars" aria-label="${n.stars} of 5 stars">${starsText(n.stars)}</span><b>${esc(n.name)}</b>${n.next !== null ? `<small>${n.points} of ${n.next} to the next star</small>` : ''}</div>`,
    );
    parts.push(`<dl class="pf-rows">${d.rows.map((r) => `<div><dt>${esc(r.label)}</dt><dd>${esc(r.value)}</dd></div>`).join('')}</dl>`);
    if (d.car) {
      parts.push(
        `<div class="pf-car"><span class="pf-cap">Your motor</span><div class="pf-car-name"><i style="--sw:${css(d.car.swatch)}" aria-hidden="true"></i><b>${esc(d.car.title)}</b></div>${d.car.mods.length ? `<ul>${d.car.mods.map((m) => `<li>${esc(m)}</li>`).join('')}</ul>` : '<ul><li>as it came</li></ul>'}${d.car.kept ? `<small>${esc(d.car.kept)}</small>` : ''}</div>`,
      );
    } else if (d.carNote) parts.push(`<div class="pf-car"><span class="pf-cap">Your motor</span><small>${esc(d.carNote)}</small></div>`);
    this.card.innerHTML = parts.join('');
    setText(this.closeBtn, '✕');
  }

  onNav(a: NavAction): boolean {
    switch (a) {
      case 'left':
        this.c.orbit(-12, 0);
        return true;
      case 'right':
        this.c.orbit(12, 0);
        return true;
      case 'up':
        this.c.orbit(0, -4);
        return true;
      case 'down':
        this.c.orbit(0, 4);
        return true;
      case 'confirm':
        if (this.clean) this.setClean(false);
        return true;
      case 'back':
        if (this.clean) {
          this.setClean(false);
          return true;
        }
        this.c.sound('back');
        this.c.close();
        return true;
    }
    return false;
  }

  dispose(): void {
    window.removeEventListener('keydown', this.onKey);
    this.root.remove();
  }
}
